import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { timeout } from 'rxjs/operators';
import * as uuid from 'uuid';
import $ from 'jquery';

import { ConfigurationService } from '../configuration/configuration.service';
import { TranslateService } from '../translate/translate.service';
import { NotifyService } from '../notify/notify.service';

@Injectable({
  providedIn: 'root'
})
export class ApiService {
  private host: string;
  private get key() {
    const apiConfiguration = this.configurationService.getApiConfiguration();
    return apiConfiguration?.localStorageKey || 'a9b9b57f-cfea-49d8-988b-9e96f486b7c4';
  }

  private tokenHeaderKey: string;

  private uploadId = 'U' + uuid.v4();
  private setCustomHeader?(headers: HttpHeaders): HttpHeaders;

  private beforeRemote?(context: {
    method: 'POST' | 'GET' | 'DELETE' | 'PUT',
    url: string,
    headers: HttpHeaders,
    body?: any
  }): void;

  private afterRemote?(context: {
    method: 'POST' | 'GET' | 'DELETE' | 'PUT',
    url: string,
    headers: HttpHeaders,
    statusCode: number,
    body?: any,
    result?: any
  }): void;

  private get token(): string {
    try {
      const { tokenHeaderValue } = JSON.parse(localStorage.getItem(this.key));
      return tokenHeaderValue;
    } catch (err) {
      return '';
    }
  }

  get defaultHeader(): HttpHeaders {
    let httpHeaders = new HttpHeaders();
    if (this.tokenHeaderKey) {
      httpHeaders = httpHeaders.set(this.tokenHeaderKey, this.token);
    }
    return httpHeaders;
  }

  private get headers() {
    if (this.setCustomHeader) {
      return this.setCustomHeader(this.defaultHeader);
    }
    return this.defaultHeader;
  }
  constructor(
    private httpClient: HttpClient,
    private notifyService: NotifyService,
    private configurationService: ConfigurationService,
    private translateService: TranslateService) {
  }

  private init = () => {
    const apiConfiguration = this.configurationService.getApiConfiguration();
    this.host = apiConfiguration.host;
    this.tokenHeaderKey = apiConfiguration.tokenHeaderKey;
    this.setCustomHeader = apiConfiguration.setCustomHeader;
    this.beforeRemote = apiConfiguration.beforeRemote;
    this.afterRemote = apiConfiguration.afterRemote;
  }

  post<T = any>(url: string, body?: any, headers?: HttpHeaders): Promise<T> {
    this.init();
    if (!url.startsWith('http')) {
      url = this.host + url;
    }
    headers = headers || this.headers;
    if (this.beforeRemote) {
      this.beforeRemote({
        url,
        method: 'POST',
        headers,
        body
      });
    }
    return new Promise<any>((resolve, reject) => {
      this.httpClient.post(encodeURI(url), body, {
        observe: 'response',
        headers
      }).pipe(timeout(600000)).toPromise().then(result => {
        if (this.afterRemote) {
          this.afterRemote({
            url,
            method: 'POST',
            headers,
            body,
            statusCode: result.status,
            result: result.body
          });
        }
        if (result.ok) {
          resolve(result.body);
        } else {
          reject(result.body);
        }
      }).catch((httpErrorResponse: HttpErrorResponse) => {
        if (this.afterRemote) {
          this.afterRemote({
            url,
            method: 'POST',
            headers,
            body,
            statusCode: httpErrorResponse.status,
            result: httpErrorResponse.error
          });
        }
        reject(httpErrorResponse);
      });
    });
  }

  put<T = any>(url: string, body?: any, headers?: HttpHeaders): Promise<T> {
    this.init();
    if (!url.startsWith('http')) {
      url = this.host + url;
    }
    headers = headers || this.headers;
    if (this.beforeRemote) {
      this.beforeRemote({
        url,
        method: 'PUT',
        headers,
        body
      });
    }
    return new Promise<any>((resolve, reject) => {
      this.httpClient.put(encodeURI(url), body, {
        observe: 'response',
        headers
      }).pipe(timeout(600000)).toPromise().then(result => {
        if (this.afterRemote) {
          this.afterRemote({
            url,
            method: 'PUT',
            headers,
            body,
            statusCode: result.status,
            result: result.body
          });
        }
        if (result.ok) {
          resolve(result.body);
        } else {
          reject(result.body);
        }
      }).catch((httpErrorResponse: HttpErrorResponse) => {
        if (this.afterRemote) {
          this.afterRemote({
            url,
            method: 'PUT',
            headers,
            body,
            statusCode: httpErrorResponse.status,
            result: httpErrorResponse.error
          });
        }
        reject(httpErrorResponse);
      });
    });
  }

  get<T = any>(url: string, headers?: HttpHeaders, options?: {
    responseType?: 'arraybuffer' | 'blob' | 'json' | 'text';
  }): Promise<T> {
    this.init();
    headers = headers || this.headers;
    if (!url.startsWith('http')) {
      url = this.host + url;
    }
    if (this.beforeRemote) {
      this.beforeRemote({
        url,
        method: 'GET',
        headers
      });
    }
    return new Promise<any>((resolve, reject) => {
      if (options?.responseType && options.responseType !== 'json') {
        this.httpClient.get(encodeURI(url), { observe: 'body', headers, responseType: options?.responseType as any })
          .toPromise().then(result => {
            if (this.afterRemote) {
              this.afterRemote({
                url,
                method: 'GET',
                headers,
                statusCode: null,
                result
              });
            }
            resolve(result);
          }).catch((httpErrorResponse: HttpErrorResponse) => {
            if (this.afterRemote) {
              this.afterRemote({
                url,
                method: 'GET',
                headers,
                statusCode: httpErrorResponse.status,
                result: httpErrorResponse.error
              });
            }
            reject(httpErrorResponse.error);
          });
      } else {
        this.httpClient.get(encodeURI(url), { observe: 'response', headers }).toPromise().then(result => {
          if (this.afterRemote) {
            this.afterRemote({
              url,
              method: 'GET',
              headers,
              statusCode: result.status,
              result: result.body
            });
          }
          if (result.ok) {
            resolve(result.body);
          } else {
            reject(result.body);
          }
        }).catch((httpErrorResponse: HttpErrorResponse) => {
          if (this.afterRemote) {
            this.afterRemote({
              url,
              method: 'GET',
              headers,
              statusCode: httpErrorResponse.status,
              result: httpErrorResponse.error
            });
          }
          reject(httpErrorResponse.error);
        });
      }
    });
  }

  delete<T = any>(url: string, headers?: HttpHeaders): Promise<T> {
    this.init();
    headers = headers || this.headers;
    if (!url.startsWith('http')) {
      url = this.host + url;
    }
    if (this.beforeRemote) {
      this.beforeRemote({
        url,
        method: 'DELETE',
        headers
      });
    }
    return new Promise<any>((resolve, reject) => {
      this.httpClient.delete(encodeURI(url), { observe: 'response', headers }).toPromise().then(result => {
        if (this.afterRemote) {
          this.afterRemote({
            url,
            method: 'DELETE',
            headers,
            statusCode: result.status,
            result: result.body
          });
        }
        if (result.ok) {
          resolve(result.body);
        } else {
          reject(result.body);
        }
      }).catch((httpErrorResponse: HttpErrorResponse) => {
        if (this.afterRemote) {
          this.afterRemote({
            url,
            method: 'DELETE',
            headers,
            statusCode: httpErrorResponse.status,
            result: httpErrorResponse.error
          });
        }
        reject(httpErrorResponse.error);
      });
    });
  }

  uploadSingle = (url: string, option?: {
    extensions?: string[],
    maxSizeInMb?: number,
    validator?: (fileName: string) => string
  }): Promise<any> => {
    return new Promise((resolve, reject) => {
      if ($(`#${this.uploadId}`).length) {
        $('body').children(`#${this.uploadId}`).remove();
      }
      $('body').append(`<input id="${this.uploadId}" style="display: none" type="file" />`);
      $(`#${this.uploadId}`).on('change', (evt: any) => {
        try {
          const target: DataTransfer = evt.target;
          const fileToUpload = target.files.item(0);
          this.upload(url, fileToUpload, option).then(resolve).catch(reject);
        } catch (error) {
          reject(error);
        }
      });
      $(`#${this.uploadId}`).trigger('click');
    });
  }

  upload = async (url: string, file: File, option?: {
    extensions?: string[],
    maxSizeInMb?: number,
    validator?: (fileName: string) => string
  }) => {
    const { translate } = this.translateService;
    const lastDot = file.name.lastIndexOf('.');
    if (lastDot === -1) {
      const message = this.translateService.translate(`Invalid file extension`);
      this.notifyService.notify.warning(message);
      throw new Error(message);
    }
    const extension = file.name.substring(lastDot + 1);
    if (option) {
      if (option.extensions?.length && !option.extensions.some(e => e.toUpperCase() === extension.toUpperCase())) {
        const message = `[${file.name}] ${translate('Uploaded file is not match extension')} ${option.extensions.join(', ')}`;
        this.notifyService.notify.warning(message);
        throw new Error(message);
      }
      if (option.maxSizeInMb > 0 && option.maxSizeInMb * 1024 * 1024 < file.size) {
        const message = `[${file.name}] ${translate('Max file size')} ${option.maxSizeInMb} Mbs`;
        this.notifyService.notify.warning(message);
        throw new Error(message);
      }
      if (option.validator && option.validator(file.name)) {
        const message = option.validator(file.name);
        this.notifyService.notify.warning(message);
        throw new Error(message);
      }
    }
    const formData: FormData = new FormData();
    formData.append('file', file, file.name);
    return await this.post(url, formData, this.defaultHeader);
  }

  setTokenHeaderValue = (tokenHeaderValue: string) => {
    localStorage.setItem(this.key, JSON.stringify({ tokenHeaderValue }));
  }

  getTokenHeaderValue = () => {
    return this.token;
  }

  get hasTokenHeaderValue() {
    return !!this.token;
  }

  clearToken() {
    localStorage.removeItem(this.key);
  }

  getHost() {
    return this.host;
  }
}
